import { useState } from 'react';
import { Save, X, Flame } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { FieldError } from '../ui/FieldError';
import { exerciseFatigue } from '../../lib/fatigue';
import { isValidUrl, inputClass } from '../../lib/validation';
import type { Exercise, ExerciseFormData, ExerciseFilters } from '../../types';

interface ExerciseFormProps {
  initialData?: Exercise;
  filters?: ExerciseFilters;
  onSubmit: (data: ExerciseFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ExerciseForm({ initialData, filters, onSubmit, onCancel, isLoading }: ExerciseFormProps) {
  const [formData, setFormData] = useState<ExerciseFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    category: initialData?.category || 'compound',
    muscleGroup: initialData?.muscleGroup || 'chest',
    equipment: initialData?.equipment,
    fatigueRating: initialData?.fatigueRating ?? null,
    videoUrl: initialData?.videoUrl || '',
    imageUrl: initialData?.imageUrl || '',
  });

  // Factor de fatiga derivado de la categoría (cuando no hay override).
  const autoFatigue = exerciseFatigue({ category: formData.category, fatigueRating: null });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.name?.trim()) e.name = 'El nombre es obligatorio';
    if (formData.videoUrl && !isValidUrl(formData.videoUrl))
      e.videoUrl = 'URL inválida (debe empezar con http:// o https://)';
    if (formData.imageUrl && !isValidUrl(formData.imageUrl))
      e.imageUrl = 'URL inválida (debe empezar con http:// o https://)';
    return e;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value || undefined,
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div>
        <label htmlFor="name" className="label">Nombre *</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          aria-invalid={!!errors.name}
          className={inputClass(!!errors.name)}
          placeholder="Nombre del ejercicio"
        />
        <FieldError message={errors.name} />
      </div>

      <div>
        <label htmlFor="description" className="label">Descripción</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={2}
          className="input resize-none"
          placeholder="Descripción del ejercicio..."
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label htmlFor="muscleGroup" className="label">Grupo Muscular *</label>
          <select
            id="muscleGroup"
            name="muscleGroup"
            value={formData.muscleGroup}
            onChange={handleChange}
            required
            className="input"
          >
            {filters?.muscleGroups?.map(mg => (
              <option key={mg.value} value={mg.value}>{mg.label}</option>
            )) || (
              <>
                <option value="chest">Pecho</option>
                <option value="back">Espalda</option>
                <option value="shoulders">Hombros</option>
                <option value="arms">Brazos</option>
                <option value="legs">Piernas</option>
                <option value="core">Core</option>
                <option value="full_body">Full Body</option>
              </>
            )}
          </select>
        </div>
        <div>
          <label htmlFor="category" className="label">Categoría *</label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
            className="input"
          >
            {filters?.categories?.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            )) || (
              <>
                <option value="basic">Básico</option>
                <option value="compound">Compuesto</option>
                <option value="isolation">Aislamiento</option>
                <option value="cardio">Cardio</option>
              </>
            )}
          </select>
        </div>
        <div>
          <label htmlFor="equipment" className="label">Equipamiento</label>
          <select
            id="equipment"
            name="equipment"
            value={formData.equipment || ''}
            onChange={handleChange}
            className="input"
          >
            <option value="">Sin especificar</option>
            {filters?.equipment?.map(eq => (
              <option key={eq.value} value={eq.value}>{eq.label}</option>
            )) || (
              <>
                <option value="barbell">Barra</option>
                <option value="dumbbell">Mancuerna</option>
                <option value="machine">Máquina</option>
                <option value="cable">Polea</option>
                <option value="bodyweight">Peso Corporal</option>
              </>
            )}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="fatigueRating" className="label flex items-center gap-1.5">
          <Flame className="w-3.5 h-3.5 text-accent-400" />
          Factor de fatiga sistémica
        </label>
        <select
          id="fatigueRating"
          name="fatigueRating"
          value={formData.fatigueRating ?? ''}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              fatigueRating: e.target.value === '' ? null : Number(e.target.value),
            }))
          }
          className="input"
        >
          <option value="">Automático según categoría (≈ {autoFatigue})</option>
          <option value="1">1 · Muy baja (aislamiento)</option>
          <option value="2">2 · Baja</option>
          <option value="3">3 · Media</option>
          <option value="4">4 · Alta (compuesto)</option>
          <option value="5">5 · Muy alta (compuesto pesado)</option>
        </select>
        <p className="text-xs text-dark-500 mt-1">
          Cuánto cuesta en recuperación. Se usa para calcular la carga de cada día.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="videoUrl" className="label">URL de Video</label>
          <input
            type="url"
            id="videoUrl"
            name="videoUrl"
            value={formData.videoUrl}
            onChange={handleChange}
            aria-invalid={!!errors.videoUrl}
            className={inputClass(!!errors.videoUrl)}
            placeholder="https://youtube.com/..."
          />
          <FieldError message={errors.videoUrl} />
        </div>
        <div>
          <label htmlFor="imageUrl" className="label">URL de Imagen</label>
          <input
            type="url"
            id="imageUrl"
            name="imageUrl"
            value={formData.imageUrl}
            onChange={handleChange}
            aria-invalid={!!errors.imageUrl}
            className={inputClass(!!errors.imageUrl)}
            placeholder="https://..."
          />
          <FieldError message={errors.imageUrl} />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-dark-700">
        <button type="button" onClick={onCancel} className="btn-secondary">
          <X className="w-4 h-4" />
          Cancelar
        </button>
        <button type="submit" disabled={isLoading} className="btn-primary">
          {isLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              {initialData ? 'Guardar' : 'Crear Ejercicio'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

